import React from 'react';
import { Tag, Database, Globe, Mail, Crown, Monitor, Server, Filter } from 'lucide-react';
import { WATCHLIST_ENTRY_TYPE_OPTIONS, WATCHLIST_STATUS_OPTIONS, WatchlistEntryType, WatchlistStatus } from '@/lib/api/types';
import CustomFilter, { FilterConfig } from '@/components/common/ui/CustomFilter';

export interface WatchlistFilters {
    search: string;
    entry_type: WatchlistEntryType | 'all';
    status: WatchlistStatus | 'all';
}

export const defaultWatchlistFilters: WatchlistFilters = {
    search: '',
    entry_type: 'all',
    status: 'all'
};

interface WatchlistFiltersProps {
    filters: WatchlistFilters;
    onFilterChange: <K extends keyof WatchlistFilters>(key: K, value: WatchlistFilters[K]) => void;
    onResetFilters: () => void;
    hasActiveFilters: boolean;
}

const getEntryTypeIcon = (type: string) => {
    switch (type) {
        case 'domain': return <Globe className="h-3.5 w-3.5" />;
        case 'email': return <Mail className="h-3.5 w-3.5" />;
        case 'vip': return <Crown className="h-3.5 w-3.5" />;
        case 'computername': return <Monitor className="h-3.5 w-3.5" />;
        case 'ip': return <Server className="h-3.5 w-3.5" />;
        default: return <Database className="h-3.5 w-3.5" />;
    }
};

const WatchlistFilters: React.FC<WatchlistFiltersProps> = ({
    filters,
    onFilterChange,
    onResetFilters,
    hasActiveFilters
}) => {
    // Main filter configurations
    const mainFilters: FilterConfig[] = [
        {
            key: 'search',
            label: 'QUERY TARGET',
            type: 'search',
            placeholder: 'Search by entry, email...',
            width: 'w-64'
        },
        {
            key: 'entry_type',
            label: 'TARGET CLASS',
            type: 'select',
            icon: <Database className="h-3.5 w-3.5 text-[#4da760]" />,
            options: [
                { value: 'all', label: 'All Classes' },
                ...WATCHLIST_ENTRY_TYPE_OPTIONS.map(option => ({
                    ...option,
                    icon: getEntryTypeIcon(option.value)
                }))
            ],
            width: 'w-48'
        },
        {
            key: 'status',
            label: 'NODE STATUS',
            type: 'select',
            icon: <Tag className="h-3.5 w-3.5 text-[#50A970]" />,
            options: [
                { value: 'all', label: 'All Statuses' },
                ...WATCHLIST_STATUS_OPTIONS
            ],
            width: 'w-48'
        }
    ];

    const handleFilterChange = (key: string, value: any) => {
        onFilterChange(key as keyof WatchlistFilters, value);
    };

    return (
        <div className="bg-[#050505] border border-[#1A1D24] rounded-xl p-5 mb-6 shadow-[0_0_30px_rgba(0,0,0,0.5)] font-mono cyber-filter-override">

            {/* Terminal Header */}
            <div className="flex items-center justify-between mb-5 pb-3 border-b border-[#1A1D24]">
                <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-[#4da760]" />
                    <h3 className="text-[12px] font-bold text-white tracking-widest uppercase">
                        Watchlist Parameter Matrix
                    </h3>
                </div>
                {hasActiveFilters && (
                    <div className="flex items-center gap-2 bg-[#4da760]/10 border border-[#4da760]/30 px-3 py-1 rounded-full">
                        <span className="h-2 w-2 rounded-full bg-[#4da760] animate-pulse"></span>
                        <span className="text-[9px] font-bold text-[#4da760] tracking-widest uppercase">Telemetry Filtered</span>
                    </div>
                )}
            </div>

            <div className="filter-component-wrapper">
                <CustomFilter
                    searchValue={filters.search}
                    onSearchChange={(value) => onFilterChange('search', value)}
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onResetFilters={onResetFilters}
                    filterConfigs={mainFilters}
                    advancedConfigs={[]}
                    showAdvanced={false}
                    onToggleAdvanced={() => { }}
                    hasActiveFilters={hasActiveFilters}
                />
            </div>

            {/* SHARED CYBER CSS INJECTION */}
            <style dangerouslySetInnerHTML={{
                __html: `
                .filter-component-wrapper > div {
                    background-color: transparent !important;
                    border: none !important;
                    box-shadow: none !important;
                    padding: 0 !important;
                }

                .cyber-filter-override {
                    font-family: monospace !important;
                }

                .cyber-filter-override .flex {
                    align-items: center !important;
                }
                
                .cyber-filter-override label, 
                .cyber-filter-override .text-sm {
                    color: #94a3b8 !important;
                    font-size: 10px !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.1em !important;
                    font-weight: bold !important;
                    margin-bottom: 6px !important;
                    display: block !important;
                }

                .cyber-filter-override input[type="text"],
                .cyber-filter-override input[type="search"], 
                .cyber-filter-override button[role="combobox"] {
                    background-color: #0A0B0E !important;
                    border: 1px solid #1A1D24 !important;
                    color: #4da760 !important;
                    font-size: 11px !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.05em !important;
                    border-radius: 6px !important;
                    height: 40px !important; 
                    box-sizing: border-box !important;
                }

                .cyber-filter-override button[role="combobox"] {
                    display: flex !important;
                    align-items: center !important;
                    justify-content: space-between !important;
                    color: #cbd5e1 !important;
                }

                .cyber-filter-override input:focus, 
                .cyber-filter-override button[role="combobox"]:focus {
                    border-color: #4da760 !important;
                    box-shadow: 0 0 15px rgba(168,85,247,0.15) !important;
                    outline: none !important;
                }

                .cyber-filter-override button:not([role="combobox"]) {
                    text-transform: uppercase !important;
                    letter-spacing: 0.1em !important;
                    font-size: 10px !important;
                    font-weight: bold !important;
                    background-color: #12151C !important;
                    border: 1px solid #1A1D24 !important;
                    color: #94a3b8 !important;
                    border-radius: 6px !important;
                    height: 40px !important;
                    padding: 0 16px !important;
                }

                .cyber-filter-override button:not([role="combobox"]):hover {
                    background-color: #161A23 !important;
                    color: #4da760 !important;
                    border-color: #4da760 !important;
                }

                /* Portal Dropdowns */
                [data-radix-popper-content-wrapper],
                div[role="presentation"] > div[role="listbox"] {
                    background-color: #0A0B0E !important;
                    border: 1px solid #1A1D24 !important;
                    font-family: monospace !important;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.9) !important;
                }

                div[role="option"] {
                    background-color: #0A0B0E !important;
                    color: #94a3b8 !important;
                    font-size: 11px !important;
                    text-transform: uppercase !important;
                    padding: 10px 32px 10px 14px !important;
                    position: relative !important;
                    display: flex !important;
                    align-items: center !important;
                }

                div[role="option"]:hover,
                div[role="option"][data-highlighted] {
                    background-color: #161A23 !important;
                    color: #4da760 !important;
                }

                div[role="option"] > span.absolute {
                    position: absolute !important;
                    right: 12px !important;
                    left: auto !important;
                }
            `}} />
        </div>
    );
};

export default WatchlistFilters;